import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorStyle,
  useColorMode,
  SearchInput,
  Stack,
  Spinner,
  ModalCloseButton,
} from '@tonic-one/react';
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrgStore } from '@Models/useOrgModel';
import { PolicyTypeEnum } from '@Policy/utils/constants';
import { PolicyHolderTypeEnum } from '@Policy/utils/types';
import ErrorAlert from '@Components/Common/ErrorAlert';
import { useApprovedTeamsMutations, useApprovedTeamsQuery } from './hooks';
import { useTeamsTargets } from './useTeamsTargets';
import Tree from '@Components/Tree';
import { TreeNode, Key } from '@Components/Tree/type';
import Dropdown from '@Components/Common/Dropdown';
import { FormLabel } from '@Components/Common/FormComponent';

interface AddTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  policyType: PolicyTypeEnum;
  currentTeamsCount: number;
}

const MAX_TEAMS = 1024;

// Helper function to flatten tree structure
const flattenTree = (nodes: TreeNode[]): TreeNode[] => {
  const result: TreeNode[] = [];

  const traverse = (node: TreeNode) => {
    result.push(node);
    if (node.children && node.children.length > 0) {
      node.children.forEach(traverse);
    }
  };

  nodes.forEach(traverse);
  return result;
};

// Helper function to mark existing teams as disabled in tree
const markExistingTeamsAsDisabled = (
  nodes: TreeNode[],
  existingTeamKeys: Set<string>
): TreeNode[] => {
  return nodes.map((node) => {
    const isExisting = existingTeamKeys.has(String(node.key));

    const updatedNode: TreeNode = {
      ...node,
      disabled: node.disabled || isExisting,
      disableCheckbox: node.disableCheckbox || isExisting,
    };

    if (node.children) {
      updatedNode.children = markExistingTeamsAsDisabled(
        node.children,
        existingTeamKeys
      );
    }

    return updatedNode;
  });
};

const AddTeamModal = ({
  isOpen,
  onClose,
  policyType,
  currentTeamsCount,
}: AddTeamModalProps) => {
  const { t } = useTranslation(['globalSetting', 'common']);
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { orgId, orgsMap } = useOrgStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<Key[]>([]);
  const [selectedPolicyType, setSelectedPolicyType] = useState<string>('atp');
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const [expandedKeys, setExpandedKeys] = useState<Key[]>(['all-teams']);

  // Debounce search term for 500ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Get current policy holder type for the data
  const currentPolicyHolderType =
    selectedPolicyType === 'atp'
      ? PolicyHolderTypeEnum.ATP
      : PolicyHolderTypeEnum.DLP;

  const { addTeamsMutation } = useApprovedTeamsMutations(policyType);

  // Use React Query hook to fetch Teams targets with backend search
  const { data: treeData = [], isLoading } = useTeamsTargets(
    orgId,
    isOpen,
    debouncedSearchTerm
  );

  // Fetch existing approved teams for the Teams service
  const { data: allExistingTeams = [] } = useApprovedTeamsQuery(policyType);

  // Filter existing teams by the selected policy holder type
  const existingTeams = useMemo(() => {
    return allExistingTeams.filter(
      (team) => team.policyType === currentPolicyHolderType
    );
  }, [allExistingTeams, currentPolicyHolderType]);

  // Create a set of existing team keys (stored in the email field) for lookup
  const existingTeamKeys = useMemo(() => {
    return new Set(existingTeams.map((team) => team.email));
  }, [existingTeams]);

  // Reset expanded keys when modal opens
  useEffect(() => {
    if (isOpen) {
      setExpandedKeys(['all-teams']);
    }
  }, [isOpen]);

  // Reset selected keys when policy type changes to clear any disabled selections
  useEffect(() => {
    setSelectedKeys([]);
    setErrorMsg(undefined);
  }, [selectedPolicyType]);

  // Mark existing teams as disabled (search is handled by backend)
  const filteredTreeData = useMemo(() => {
    return markExistingTeamsAsDisabled(treeData, existingTeamKeys);
  }, [treeData, existingTeamKeys]);

  // Handle checkbox selection
  const handleCheck = (checkedKeys: Key[]) => {
    // Filter out parent nodes and special nodes, only keep leaf nodes
    const leafKeys = checkedKeys.filter((key) => {
      const isParent = key === 'all-teams';
      const isShowMore = key === 'show-more';
      return !isParent && !isShowMore;
    });

    // Check if we exceed the limit
    if (currentTeamsCount + leafKeys.length > MAX_TEAMS) {
      setErrorMsg(
        t('globalSetting.approvedTeams.addModal.error.maxTeams', {
          count: MAX_TEAMS,
        })
      );
      return;
    }

    setSelectedKeys(leafKeys);
    setErrorMsg(undefined);
  };

  // Handle save
  const handleSave = async () => {
    if (selectedKeys.length === 0) {
      return;
    }

    try {
      setErrorMsg(undefined);

      // Get selected nodes data
      const allNodes = flattenTree(treeData);
      const selectedNodes = allNodes.filter(
        (node) => selectedKeys.includes(node.key) && node.isLeaf
      );

      const users = selectedNodes.map((node) => ({
        name: (node.title?.toString() as string) || '', // Team display name
        companyGuid: orgId,
        policyType: currentPolicyHolderType,
        email: String(node.key), // Team identifier
      }));

      await addTeamsMutation.mutateAsync(users);
      onClose();
    } catch (error: any) {
      setErrorMsg(error.message || t('common.apiException.internalError'));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('globalSetting.approvedTeams.addModal.title')}
        </ModalHeader>
        <ModalBody>
          <Stack gap="4x">
            {errorMsg && (
              <Box>
                <ErrorAlert
                  errorMsg={errorMsg}
                  onClose={() => setErrorMsg(undefined)}
                />
              </Box>
            )}

            <Box>
              <Text fontSize="sm" color={colorStyle.color.secondary}>
                {t('globalSetting.approvedTeams.addModal.description')}
              </Text>
            </Box>

            <Flex alignItems="center">
              <FormLabel
                label={t('globalSetting.approvedUser.column.policyType')}
                mr="2x"
              />
              <Dropdown
                options={['atp', 'dlp']}
                value={selectedPolicyType}
                onChange={setSelectedPolicyType}
                renderOption={(option) => {
                  return t(
                    `globalSetting.approvedUser.table.policyType.${option}`
                  );
                }}
                width={270}
              />
            </Flex>

            <Box>
              <Text>
                {t('common.label.currentOrganization')}: {orgsMap.get(orgId)}
              </Text>
            </Box>

            <Box>
              <SearchInput
                placeholder={t('log.button.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClearInput={() => setSearchTerm('')}
                mb="2x"
                width="100%"
              />

              <Box
                position="relative"
                height="300px"
                overflow="hidden"
                border="1px solid"
                borderColor={colorStyle.divider}
                borderRadius="sm"
                p="2x"
              >
                {filteredTreeData.length === 0 && !isLoading ? (
                  <Flex
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                  >
                    <Text color={colorStyle.color.secondary}>
                      {t('common.noData')}
                    </Text>
                  </Flex>
                ) : (
                  <Tree
                    checkable
                    checkedKeys={selectedKeys}
                    onCheck={handleCheck}
                    defaultExpandedKeys={['all-teams']}
                    expandedKeys={expandedKeys}
                    onExpand={setExpandedKeys}
                    treeData={filteredTreeData}
                    showIcon={false}
                    style={{ height: '280px' }}
                    defaultExpandAll={false}
                    onSelect={(_: Key[], info: any) => {
                      // Handle "Show more" click
                      const node = info.node;
                      if (node && node.isShowMore && node.onSelect) {
                        node.onSelect();
                      }
                    }}
                  />
                )}

                {/* Loading overlay */}
                {isLoading && (
                  <Flex
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    justifyContent="center"
                    alignItems="center"
                    bg={
                      colorMode === 'dark'
                        ? 'rgba(0, 0, 0, 0.7)'
                        : 'rgba(255, 255, 255, 0.8)'
                    }
                    borderRadius="sm"
                    zIndex="1"
                  >
                    <Spinner size="lg" />
                  </Flex>
                )}
              </Box>
            </Box>
          </Stack>
        </ModalBody>
        <ModalFooter gap="2x">
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={selectedKeys.length === 0 || addTeamsMutation.isPending}
          >
            {t('common.button.save')}{' '}
            {selectedKeys.length > 0 && `(${selectedKeys.length})`}
          </Button>
          <Button onClick={onClose}>{t('common.button.cancel')}</Button>
        </ModalFooter>
        <ModalCloseButton />
      </ModalContent>
    </Modal>
  );
};

export default AddTeamModal;
